import React, { useState, useEffect } from 'react';
import { Translate } from '../../shared/LanguageService';
import {
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    Autocomplete,
    Chip,
    SelectChangeEvent
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IPhotographer from './IPhotographer';
import PhotographersApi from '../photographers/PhotographersApi';
import WysiwygEditor from '../../components/common/editor/Editor';
import {
    getEmailTemplates,
    getEmailSenders
} from '../photographerCalendars/actions/CalendarActions';
import { connect } from 'react-redux';

const MAIL_TO_BROKER_AGENT_TEMPLATE_KEY = 15;
const MAIL_FROM_DEFALUT = 'bilder@se360.se';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface ISendEmailToPhotographersModalProps {
    open: boolean;
    photographers: IPhotographer[];
    onClose: () => void;
    actions: {
        getTemplates: (photographerId: number) => Promise<any>;
        getSenders: () => Promise<any>;
    };
    templates: any[];
    senders: any[];
}

const SendEmailToPhotographersModal: React.FC<ISendEmailToPhotographersModalProps> = ({
    open,
    photographers,
    onClose,
    actions,
    templates,
    senders
}) => {
    const [bcc, setBcc] = useState('');
    const [sender, setSender] = useState('');
    const [subject, setSubject] = useState('');
    const [templateName, setTemplateName] = useState('');
    const [templateKey, setTemplateKey] = useState(0);
    const [body, setBody] = useState('');

    const [selectedPhotographers, setSelectedPhotographers] = useState<IPhotographer[]>([]);
    const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
    const [showFailureSnackbar, setShowFailureSnackbar] = useState(false);
    const [loading, setLoading] = useState(false);

    const setDefaultSender = () => {
        const defaultSenderExists = senders.some(
            (s) => s.emailAddresses[0].address === MAIL_FROM_DEFALUT
        );
    
        if (!defaultSenderExists) {
            senders.unshift({
                id: 'default',
                name: 'Default Sender',
                emailAddresses: [{ address: MAIL_FROM_DEFALUT }],
            });
        }
    
        if (!sender) {
            setSender(MAIL_FROM_DEFALUT);
        }
    };

    const setDefaultTemplate = () => {
        if (templates && templates.length > 0) {
            const defaultTemplate = templates.find(
                (t) => t.key === MAIL_TO_BROKER_AGENT_TEMPLATE_KEY
            );
            if (defaultTemplate) {
                setBody(defaultTemplate.content);
                setTemplateName(defaultTemplate.name);
                setTemplateKey(defaultTemplate.key);
            }
        }
    };

    useEffect(() => {
        if (!open || (senders.length > 0 && templates.length > 0)) return;
    
        setLoading(true);
    
        Promise.all([
            senders.length === 0
                ? actions.getSenders().catch((error) => console.error('Error fetching senders:', error))
                : Promise.resolve(),
            templates.length === 0
                ? actions
                      .getTemplates(photographers.length > 0 ? photographers[0].id : undefined)
                      .catch((error) => console.error('Error fetching templates:', error))
                : Promise.resolve(),
        ]).finally(() => {
            setDefaultSender();
            setLoading(false);
        });
    }, [open, photographers, actions, senders, templates]);

    useEffect(() => {
        if (templates && templates.length > 0) {
            setDefaultTemplate();
        }
    }, [templates]);

    // Pre-select all photographers
    useEffect(() => {
        if (photographers && photographers.length > 0) {
            setSelectedPhotographers([...photographers]);
        }
    }, [photographers]);

    const handleTemplateChange = (event: SelectChangeEvent<string>) => {
        const selectedTemplateName = event.target.value;
        const selectedTemplate = templates.find((t) => t.name === selectedTemplateName);

        if (selectedTemplate && selectedTemplate.content) {
            setBody(selectedTemplate.content);
            setTemplateKey(selectedTemplate.key);
        } else {
            setBody('');
            setTemplateKey(0);
        }

        setTemplateName(selectedTemplateName);
    };

    const handleSendEmail = async () => {
        try {
            const payload = {
                sender,
                subject,
                body,
                recipients: selectedPhotographers.map((p) => ({
                    id: p.id,
                    name: p.name,
                    email: p.email
                }))
            };

            const response = await PhotographersApi.sendEmailToPhotographers(payload);

            if (response.status === 200) {
                setShowSuccessSnackbar(true);
            } else {
                setShowFailureSnackbar(true);
            }
        } catch (error) {
            console.error('Error sending email:', error);
            setShowFailureSnackbar(true);
        } finally {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle>{Translate('photographer/send-email-by-region/modal/title')}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    {/* From, Sender */}
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel id="sender-select-label">
                                {Translate('photographer-mail-modal/sender')}
                            </InputLabel>
                            <Select
                                labelId="sender-select-label"
                                label={Translate('photographer-mail-modal/sender')}
                                value={sender}
                                onChange={(e) => setSender(e.target.value)}
                            >
                                {senders &&
                                    senders.map((s) => (
                                        <MenuItem key={s.id} value={s.emailAddresses[0].address}>
                                            {`${s.name} <${s.emailAddresses[0].address}>`}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Templates */}
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel id="template-select-label">
                                {Translate('photographer-mail-modal/template')}
                            </InputLabel>
                            <Select
                                labelId="template-select-label"
                                label={Translate('photographer-mail-modal/template')}
                                value={templateName}
                                onChange={handleTemplateChange}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {templates &&
                                    templates.map((t, index) => (
                                        <MenuItem key={t.id || index} value={t.name}>
                                            {t.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* To, Receivers, Photographers */}
                    <Grid item xs={12}>
                        <Autocomplete
                            multiple
                            id="to-photograpers-recepients"
                            options={photographers}
                            disableCloseOnSelect
                            getOptionLabel={(option) => `${option.name} <${option.email}>`}
                            value={selectedPhotographers}
                            onChange={(event, newValue) => {
                                const filteredValue = newValue.filter(
                                    (item): item is IPhotographer => typeof item !== 'string'
                                );
                                setSelectedPhotographers(filteredValue);
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option, { selected }) => {
                                return (
                                    <li key={option.id} {...props}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {`${option.name} <${option.email}>`}
                                    </li>
                                );
                            }}
                            renderInput={(params) => {
                                return (
                                    <TextField
                                        {...params}
                                        label="BCC Recepients"
                                        placeholder="BCC"
                                    />
                                );
                            }}
                            renderTags={(value, getTagProps) => {
                                return value.map((option, index) => {
                                    return (
                                        <Chip
                                            variant="outlined"
                                            label={
                                                option.name ||
                                                `Name missing (ID: ${option.id || 'N/A'})`
                                            }
                                            {...getTagProps({ index })}
                                            style={{ zIndex: 10 }}
                                        />
                                    );
                                });
                            }}
                        />
                    </Grid>

                    {/* Subject */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={Translate('photographer-mail-modal/subject')}
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </Grid>

                    {/* Body */}
                    <Grid item xs={12}>
                        <WysiwygEditor
                            key={`mail-editor-${templateKey}`}
                            onChange={(content) => setBody(content)}
                            content={body}
                            height={300}
                            addImageModal={false}
                            onImageUploaded={() => {}}
                            onAttachmentUpload={() => Promise.resolve()} // Provide a valid function
                            onAttachmentDelete={() => Promise.resolve()} // Provide a valid function
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    {Translate('photographer/send-email-by-region/modal/buttons/close')}
                </Button>
                <Button onClick={handleSendEmail} variant="contained" color="primary">
                    {Translate('photographer/send-email-by-region/modal/buttons/send-email')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

function mapStateToProps(state: any, ownProps: any) {
    return {
        templates: state.photographerCalendars.emailTemplatesForPhotographer || [],
        senders: state.photographerCalendars.emailSendersForPhotographer || []
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            getTemplates: (photographerId: number) => dispatch(getEmailTemplates(photographerId)),
            getSenders: () => dispatch(getEmailSenders())
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(SendEmailToPhotographersModal);
